import React, { useEffect, useState } from 'react'
import { getBaseUrl } from '../../Utils'
import { FoundationsInput } from '../../GenericComponents/input'
import { FoundationsButton } from '../../GenericComponents/button'
import FoundationsSelect from '../../GenericComponents/select'
import DatePicker from '../../Components/DatePicker/DatePicker'
import moment from 'moment'
import 'moment-timezone'
import {
    useGetUsersForActivityMutation,
    useGetActivityDataMutation,
} from '../../Redux/Admin_Tasks/ManageActivitySlice'
import GenericTableforArrayofObjects from '../../GenericComponents/GenericTableforArrayofObjects'
import { useTitleContext } from "../../Hooks/useTitleContext";
import { useSelector } from 'react-redux'
import { getCurrentUserData, RootState } from '../../Redux/store'
import { FoundationsConstants } from '../../FoundationsConstants'
import { DarkModeColors,LightModeColors } from '../../../src/Components/ReactSelect'

export interface AuditApiDTO_AuditData {
    auditFrom: string
    auditTo: string
    auditUser: number
    auditMessage: string
    pageNumber: number
    pageSize: number
}

const initialAuditData: AuditApiDTO_AuditData = {
    auditFrom: '',
    auditTo: '',
    auditUser: -3,
    auditMessage: '',
    pageNumber: 1,
    pageSize: 200,
}

const ViewActivityAndAudits: React.FC = () => {

    const { setTitle } = useTitleContext();
    setTitle("ManageAudit");

    const [auditData, setAuditData] = useState<AuditApiDTO_AuditData>(initialAuditData)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [users, setUsers] = useState([])
    const [activityData, setActivityData] = useState<any[]>([])
    const [hasFetched, setHasFetched] = useState(false);
    const [isNightMode, setIsNightMode] = useState(document.body.classList.contains('night-mode'))
    const [
        GetUsers,
        { isLoading: GetUsersLoading, isSuccess: GetUsersSuccess, error: GetUsersError },
    ] = useGetUsersForActivityMutation()
    const [
        GetActivityData,
        { isLoading: GetActivityLoading, isSuccess: GetActivitySuccess, error: GetActivityError },
    ] = useGetActivityDataMutation()

    const handleSearch = () => {
        if (GetActivityLoading) {
            return
        }
        const fromDate = new Date(auditData.auditFrom)
        const toDate = new Date(auditData.auditTo)

        if (toDate < fromDate) {
            setError("'Audit To' date cannot be earlier than 'Audit From' date.")
            return false
        } else {
            setError('')
        }
        setLoading(true)

        GetActivityData(auditData)
            .unwrap()
            .then((res) => {
                const filteredData = sortAuditObject(res.data.AuditData)
                setActivityData(filteredData)
                setHasFetched(true)
            })
        .finally(() => {
            setLoading(false)
        })
    }

    const handleClear = () => {
        setAuditData(initialAuditData)
        setError('')
    }

    const sortAuditObject = (auditData: any[]): any[] =>
        auditData.map(
            ({
                pageSize,
                pageNumber,
                Links,
                Warning,
                MessageResults,
                FirstName,
                LastName,
                AuditType,
                AuditMessage,
                AuditedOn,
                ...rest
            }) => {
                const AuditedUser = `${FirstName} ${LastName}`
                return {
                    'Audited On': AuditedOn,
                    'Audited User': AuditedUser,
                    'Audit Type': AuditType,
                    'Audit Message': AuditMessage,
                    ...rest,
                }
            }
        )
    const currentUserData = useSelector((state: RootState) => getCurrentUserData(state))
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const condition = { conditionTypeEnum: 1, conditionData: 'Approved' }
                const formData = new FormData()
                Object.entries(condition).forEach(([key, value]) => {
                    formData.append(key, value.toString())
                })
                const [userResponse, activityResponse] = await Promise.all([
                    GetUsers(formData).unwrap(),
                    GetActivityData(auditData).unwrap(),
                ])
                const userOptions = userResponse.data.map(
                    (user: { id: string; displayName: string; emailAddress: string }) => ({
                        value: user.id,
                        label: `${user.displayName} (${user.emailAddress})`,
                    })
                )

                setUsers(userOptions)

                const filteredData = sortAuditObject(activityResponse.data.AuditData)
                setActivityData(filteredData)
                setHasFetched(true)
            } catch (error) {
                console.error('Error fetching data:', error)
                setUsers([])
                setActivityData([])
                setHasFetched(true)
            } finally {
                setLoading(false);
            }
        }

        fetchData()
    }, [])

    const customStyles = (isDarkMode: boolean) => {
    const textColor = isDarkMode ? DarkModeColors.textColorWhite : LightModeColors.textColorBlack;
    const greyColor = (isDarkMode ? DarkModeColors.grey : LightModeColors.grey) || 'grey';
    const placeholderColor = isDarkMode ? DarkModeColors.textColorWhite : LightModeColors.Grey2 || '#888';
    const selectedBg = isDarkMode ? DarkModeColors.ListItemSelectedGrey : LightModeColors.lightGrey;
    const focusedBg = isDarkMode ? DarkModeColors.DarkBlue : LightModeColors.LightBlue1;
    const baseBg = isDarkMode ? DarkModeColors.AdminTasksGrey : LightModeColors.textColorWhite;

    return {
        control: (provided: any,state: any) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#495057' : '#ffffff',
            border: `1px solid ${greyColor}`,
            borderRadius: '0px',
            color: textColor,
            height: '38px',
            minHeight: '38px',
            alignItems: 'center',
            boxShadow: state.isFocused ? 'none' : provided.boxShadow,
        }),
        input: (base: any) => ({
            ...base,
            color: textColor,
            fontSize: '16px',
            margin: 0,
        }),
        placeholder: (base: any) => ({
            ...base,
            color: placeholderColor,
            marginBottom: '10px',
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
            marginBottom: 2,
            minHeight: '27px',
            height: '27px',
            backgroundColor: isDarkMode ? DarkModeColors.AdminTasksGrey : undefined,
        }),
        indicatorsContainer: (provided: any) => ({
            ...provided,
            height: '30px',
        }),
        dropdownIndicator: (provided: any) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: '4px',
    color: isDarkMode ? DarkModeColors.textColorWhite : DarkModeColors.lightGrey,
    ':hover': {
        color: isDarkMode ? DarkModeColors.textColorWhite : DarkModeColors.lightGrey, // no color change on hover
    },
}),
        clearIndicator: (provided: any) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: '5px',
            color: isDarkMode ? DarkModeColors.textColorWhite : LightModeColors.textColorBlack,
            ':hover': {
              color: isDarkMode ? DarkModeColors.textColorWhite : LightModeColors.textColorBlack,
            },
        }),
        indicatorSeparator: (provided: any) => ({
  ...provided,
  height: '20px',
  backgroundColor: isDarkMode ? DarkModeColors.lightGrey : LightModeColors.grey,
}),

        menu: (provided: any) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
        }),
        menuList: (provided: any) => ({
          ...provided,
          backgroundColor: isDarkMode ? DarkModeColors.AdminTasksGrey : LightModeColors.textColorWhite,
          scrollbarColor: isDarkMode ? `${DarkModeColors.lightGrey} transparent` : undefined,
          '&::-webkit-scrollbar-thumb': {
            background: isDarkMode ? DarkModeColors.lightGrey : LightModeColors.lightGrey,
            borderRadius: '4px',
          },
        }),
        singleValue: (base: any) => ({
            ...base,
            color: textColor,
            borderColor: greyColor,
        }),
        option: (provided: any, state: any) =>{
            const { isDisabled, isFocused, isSelected } = state;
            const bgColor = isSelected ? selectedBg : isFocused ? focusedBg : baseBg;
            const optTextColor = isDisabled ? (isDarkMode ? DarkModeColors.lightGrey : LightModeColors.DarkGrey) : textColor;

            return {
                ...provided,
                backgroundColor: bgColor,
                color: optTextColor,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                ':active': { backgroundColor: bgColor },
            };
        },
    };
};

    useEffect(() => {
        const handleClassChange = () => {
            setIsNightMode(document.body.classList.contains('night-mode'))
        }

        const observer = new MutationObserver(() => {
            handleClassChange()
        })

        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })

        return () => {
            observer.disconnect()
        }
    }, [])

    return (
        <>
            <div>
                <div className="float-right margin-top-small">
                    <a
                        id="link_backto_admin_tasks"
                        href="#AdminTasks"
                    >
                        &laquo; Back to Admin Tasks
                    </a>
                </div>
                <div className="header">View Activity and Audits</div>

                <div className="audit-filter" style={{ marginTop: '1rem' }}>
                    <div className="filter-header h5 ml-3">Filter</div>
                    <div className="filter-content">
                        <div className="filter-item">
                            <label className="label">Audit From:</label>
                            <DatePicker
                                dateFormat="MM/DD/YYYY h:mm a"
                                classes="custom-datepicker"
                                showIcon={false}
                                startingDate={
                                    auditData.auditFrom
                                        ? moment(auditData.auditFrom, 'MM/DD/YYYY hh:mm a').toDate()
                                        : null
                                }
                                setStartingDate={(value: any) => {
                                    const formattedDate = value ? moment(value).format('MM/DD/YYYY h:mm a') : ''; // FP-10571 : Pass null value when backspace clicked
                                    setAuditData({
                                        ...auditData,
                                        auditFrom: formattedDate,
                                    });
                                }}
                                showTime={true}
                                interval={30}
                            />
                        </div>
                        <div className="filter-item">
                            <label className="label">Audit To:</label>
                            <DatePicker
                                dateFormat="MM/DD/YYYY h:mm a"
                                classes="custom-datepicker"
                                showIcon={false}
                                startingDate={
                                    auditData.auditTo
                                        ? moment(auditData.auditTo, 'MM/DD/YYYY hh:mm a').toDate()
                                        : null
                                }
                                setStartingDate={(value: any) => {
                                    const formattedDate = value ? moment(value).format('MM/DD/YYYY h:mm a') : '';
                                    setAuditData({
                                        ...auditData,
                                        auditTo: formattedDate,
                                    });
                                }}
                                showTime={true}
                                interval={30}
                            />
                        </div>
                        <div className="filter-item">
                            <label className="label">Audit User:</label>
                            <FoundationsSelect
                                value={auditData.auditUser}
                                onChange={(selectedOption: any) => {
                                    setAuditData({
                                        ...auditData,
                                        auditUser: selectedOption?.value ?? -3,
                                    })
                                }}
                                id="userSelect"
                                isSearchable={true}
                                isMultiSelect={false}
                                isClearable={true}
                                options={users}
                                placeHolder="Select User"
                                customStyle={{ width: '15rem' }}
                                selectCustomStyles={customStyles(currentUserData?.DisplayMode === FoundationsConstants.DisplayMode.Night)}
                                isLoading={GetUsersLoading}
                            />
                        </div>
                        <div className="filter-item " style={{ marginLeft: '3rem' }}>
                            <label className="label">Audit Message:</label>
                            <input
                                type="text"
                                id="filter_search"
                                value={auditData.auditMessage}
                                onInput={(e: any) =>
                                    setAuditData({ ...auditData, auditMessage: e.target.value })
                                }
                                maxLength={200}
                                style={{ width: '25rem' }}
                                className="react-audit-message"
                                //inputClasses='react-audit-message'
                            />
                        </div>
                        <div style={{ marginLeft: '35px', marginTop: '19px', display: 'flex' }}>
                            <FoundationsButton
                                type="submit"
                                buttonClasses="app-button"
                                customStyle={{ marginRight: '24px' }}
                                onClick={handleSearch}
                                id={''}
                            >
                                Search
                            </FoundationsButton>
                            <FoundationsButton
                                type="button"
                                buttonClasses="app-button negative"
                                onClick={handleClear}
                                id={''}
                            >
                                Clear
                            </FoundationsButton>
                            {loading && (
                              <div className="ajax-loading-box" style={{ marginLeft: '6px' }}>
                                <span className="ajax-loading-image"></span>
                                <span>Searching...</span>
                              </div>
                            )}
                        </div>
                    </div>
                    <div id="error-container" className="block clear-both field-validation-error">
                        {error}
                    </div>
                </div>
            </div>
            <div className="audit-data">
                {!hasFetched ? null : activityData.length > 0 ? (
                    <GenericTableforArrayofObjects items={activityData} />
                ) : (
                    <table className="admin-table" style={{ marginTop: '20px' }}>
                        <thead>
                            <tr className="admin-row-header admin-alt-row-data">
                                <th style={{ width: '13%', textAlign: 'left' }}>Audited On</th>
                                <th style={{ width: '13%', textAlign: 'left' }}>Audit User</th>
                                <th style={{ width: '10%', textAlign: 'left' }}>Audit Type</th>
                                <th style={{ width: '64%', textAlign: 'left' }}>Audit Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="admin-alt-row-data" style={{ textAlign: 'left', color: 'inherit' }}>
                                <td colSpan={4}>
                                    No audit data found    
                                </td>                          
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
        </>
    )
}

export default ViewActivityAndAudits
